import { useState, useEffect } from 'react';
import { type Page, PageItemType } from '../types';
import { PageItem } from './page-items/PageItem';
import { normalizePageItemSettings } from '@/utils/normalizePageItemSettings';

const MINUTE_IN_MS = 60 * 1000;

function getInvertSettings(settings: any) {
  const start = settings?.invertColorsTimeRange?.start;
  const end = settings?.invertColorsTimeRange?.end;
  const invertColors = settings?.invertColors === true;

  return {
    invertColors,
    start,
    end,
  };
}

function isInvertActive(now: Date, invertColors?: boolean, start?: string, end?: string): boolean {
  if (!invertColors) return false;
  if (!start || !end) return true;
  const [startH, startM] = start.split(':').map(Number);
  const [endH, endM] = end.split(':').map(Number);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;
  if (startMinutes <= endMinutes) {
    return nowMinutes >= startMinutes && nowMinutes < endMinutes;
  }
  return nowMinutes >= startMinutes || nowMinutes < endMinutes;
}

interface PageProps {
  page: Page;
  isActive?: boolean;
}

const rotationStyle = {
  [180]: {
    transform: 'rotate(180deg)',
  },
  [90]: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    transform: 'rotate(90deg) translateY(-100%)',
    WebkitTransform: 'rotate(90deg) translateY(-100%)',
    MozTransform: 'rotate(90deg) translateY(-100%)',
    MozTransformOrigin: 'top left',
    WebkitTransformOrigin: 'top left',
    transformOrigin: 'top left',
    width: '100vh',
    height: '100vw',
  },
  [270]: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    transform: 'rotate(270deg) translateX(-100%)',
    WebkitTransform: 'rotate(270deg) translateX(-100%)',
    MozTransform: 'rotate(270deg) translateX(-100%)',
    MozTransformOrigin: 'top left',
    WebkitTransformOrigin: 'top left',
    transformOrigin: 'top left',
    width: '100vh',
    height: '100vw',
  },
} as const;

export function Page({ page, isActive }: PageProps) {
  const isSingleVideo = page?.pageItems?.length === 1 && (page?.pageItems?.[0]?.type as any) === PageItemType.Video && page.rotation !== 0;

  const hasAnyInvert = page?.pageItems?.some((pageItem) => {
    const settings = normalizePageItemSettings(pageItem.type, pageItem.settings);
    return getInvertSettings(settings).invertColors;
  });
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!hasAnyInvert) return;

    let intervalId: ReturnType<typeof setInterval> | undefined;
    const tick = () => setNow(new Date());
    const msUntilNextMinute = MINUTE_IN_MS - (Date.now() % MINUTE_IN_MS);

    tick();

    const timeoutId = setTimeout(() => {
      tick();
      intervalId = setInterval(tick, MINUTE_IN_MS);
    }, msUntilNextMinute);

    return () => {
      clearTimeout(timeoutId);

      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [hasAnyInvert]);

  return (
    <div className="relative w-screen h-screen">
      <div className="relative w-screen h-screen" style={{ ...(!isSingleVideo ? rotationStyle[(page?.rotation ?? 0) as keyof typeof rotationStyle] ?? {} : {}) }}>
        {page?.pageItems?.map((pageItem) => {
          const settings = normalizePageItemSettings(pageItem.type, pageItem.settings);
          const { invertColors, start, end } = getInvertSettings(settings);
          const inverted = isInvertActive(now, invertColors, start, end);

          return (
            <div
              key={pageItem.pageItemId}
              className="card"
              style={{
                position: 'absolute',
                top: `${pageItem.posY}%`,
                left: `${pageItem.posX}%`,
                width: `${pageItem.width}%`,
                height: `${pageItem.height}%`,
                zIndex: pageItem.zindex ?? 1,
              }}
            >
              <PageItem
                pageItem={pageItem}
                rotation={isSingleVideo ? page?.rotation : 0}
                isActive={isActive}
                inverted={inverted}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
